import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormGroup, FormControl, FormCheck } from 'react-bootstrap';
import { FormInputHelp, FormInputLabel } from 'blaze-form';
import FormInputCalendarItem from './FormInputCalendarItem';

const getTimeUnitTypes = plural => {
  const suffix = plural ? 's' : '';

  return [
    { label: `Year${suffix}`, value: 'year' },
    { label: `Month${suffix}`, value: 'month' },
    { label: `Quarter${suffix}`, value: 'quarter' },
    { label: `Week${suffix}`, value: 'week' },
    { label: `Day${suffix}`, value: 'day' },
  ];
};

const startOptions = [
  { label: 'Start of', value: 'startOf' },
  { label: 'End of', value: 'endOf' },
];

const masterOptions = [
  { label: 'Current Month', value: 'CM' },
  { label: 'Current Date', value: 'CD' },
  { label: 'First Day of Current Month', value: 'BM' },
  { label: 'Last Day of Current Month', value: 'EM' },
  { label: 'Current Year', value: 'CY' },
  { label: 'Day of Month', value: 'DOM' },
  { label: 'Last Day of Month', value: 'LDOM' },
  { label: 'Current Month-Year', value: 'CMY' }, // 'current?'
  { label: 'Date Using Current Month, ## as Day and Current Year', value: 'DM' },
];

const FormInputRuntimeDate = props => {
  const { controlId, field, disabled, noLabel, trackDirty, hidden } = props;

  let runtimeValues;

  try {
    runtimeValues = JSON.parse(props.value);
  } catch (e) {
    runtimeValues = props.value || {};
  }

  const hasRuntime = !!runtimeValues.startValue;

  const [dirty, setDirty] = useState(props.value !== props.originalValue);
  const [touched, setTouched] = useState(props.touched);
  const [errorMessage, setErrorMessage] = useState(props.errorMessage);
  const [originalValue, setOriginalValue] = useState(props.originalValue);
  const [valid, setValid] = useState(props.valid);

  const [startValue, setStartValue] = useState(runtimeValues.startValue || 'startOf');
  const [startType, setStartType] = useState(runtimeValues.startType || 'year');
  const [operator, setOperator] = useState(runtimeValues.operator || '+');
  const [unitValue, setUnitValue] = useState(runtimeValues.unitValue || 0);
  const [unitType, setUnitType] = useState(runtimeValues.unitType || 'year');

  const [staticValue, setStaticValue] = useState(hasRuntime ? '' : runtimeValues.staticValue || '');
  const [showRuntime, setShowRuntime] = useState(hasRuntime);
  const firstUpdate = useRef(true);
  const rv = useRef(runtimeValues);

  const useOptions = [{ value: '', label: '' }];
  if (field.customOptions && field.customOptions.length) {
    masterOptions.forEach(option => {
      if (field.customOptions.some(o => o === option.value)) {
        useOptions.push(option);
      }
    });
  }

  const handleSwitch = event => {
    setShowRuntime(event.target.checked);
  };

  const handleStaticChange = (name, newValue) => {
    setStaticValue(newValue);
  };

  useEffect(() => {
    const values = rv.current;
    // Fix for old data formats
    if (props.value && !values.startValue && !values.staticValue) {
      props.onChange(field.name, { staticValue: props.value });
    }
  }, []);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const problems = [];
    const newValue = {
      startValue,
      startType,
      operator,
      unitType,
      unitValue,
    };

    props.onChange(field.name, newValue);

    setDirty(originalValue !== newValue);
    setTouched(true);

    if (!/^\d+$/.test(unitValue)) {
      problems.push(`Invalid unit value ${unitValue}.`);
    }

    if (problems.length) {
      setValid(false);
      setErrorMessage(problems.join(' '));
      return;
    }

    setValid(true);
  }, [startValue, startType, operator, unitValue, unitType]);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }

    const newValue = {
      staticValue,
    };

    props.onChange(field.name, newValue);
  }, [staticValue]);

  useEffect(() => {
    setOriginalValue(props.originalValue);
  }, [props.originalValue]);

  useEffect(() => {
    const newValue = {
      startValue,
      startType,
      operator,
      unitType,
      unitValue,
    };
    const useValue = showRuntime ? newValue : { staticValue };

    props.onChange(field.name, useValue);
  }, [showRuntime]);

  const isDirty = dirty && valid && !field.readOnly && trackDirty;
  const isReadOnly = field.readOnly || disabled;
  const isInvalid = !valid && touched;

  const containerClass = classNames({
    'form-select': true,
    'is-invalid': isInvalid,
    'is-dirty': isDirty,
    'is-readonly': isReadOnly,
    'is-hidden': hidden,
  });

  return (
    <FormGroup controlId={controlId} className={containerClass} key={controlId} id={`${controlId}-control`}>
      <FormInputLabel hide={noLabel}>{field.label}</FormInputLabel>
      {!showRuntime && (
        <FormInputCalendarItem
          controlId={`${controlId}-static`}
          onChange={handleStaticChange}
          field={{ type: 'date' }}
          value={staticValue}
          originalValue={props.originalValue}
        />
      )}
      {showRuntime && (
        <>
          <FormControl
            as="select"
            onChange={e => setStartValue(e.target.value)}
            readOnly={field.readOnly}
            required={field.required}
            style={{ display: 'inline-block', width: '25%' }}
            value={startValue}
          >
            {startOptions.map(option => {
              return (
                <option value={option.value} key={option.value}>
                  {option.label}
                </option>
              );
            })}
          </FormControl>
          <div style={{ display: 'inline-block', width: '25%', paddingLeft: '10px' }}>
            <FormControl
              as="select"
              onChange={e => setStartType(e.target.value)}
              readOnly={field.readOnly}
              required={field.required}
              value={startType}
            >
              {getTimeUnitTypes().map(option => {
                return (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </FormControl>
          </div>
          <div style={{ display: 'inline-block', width: '15%', padding: '0px 10px' }}>
            <FormControl
              as="select"
              disabled={isReadOnly}
              onChange={e => setOperator(e.target.value)}
              readOnly={field.readOnly}
              required={field.required}
              value={operator}
            >
              {['+', '-'].map(option => {
                return (
                  <option value={option} key={option}>
                    {option}
                  </option>
                );
              })}
            </FormControl>
          </div>
          <FormControl
            disabled={isReadOnly}
            onChange={e => setUnitValue(e.target.value)}
            readOnly={field.readOnly}
            required={field.required}
            value={unitValue}
            style={{ display: 'inline-block', width: '10%', textAlign: 'center' }}
          />
          <div style={{ display: 'inline-block', width: '25%', paddingLeft: '10px' }}>
            <FormControl
              as="select"
              onChange={e => setUnitType(e.target.value)}
              readOnly={field.readOnly}
              required={field.required}
              value={unitType}
            >
              {getTimeUnitTypes(unitValue > 1).map(option => {
                return (
                  <option value={option.value} key={option.value}>
                    {option.label}
                  </option>
                );
              })}
            </FormControl>
          </div>
          <FormInputHelp errorText={errorMessage}>{field.helpText}</FormInputHelp>
        </>
      )}
      <div className="switch-wrapper">
        <FormCheck className="switch-control pl-0">
          <FormCheck.Input type="checkbox" id={`chk-${controlId}`} onChange={handleSwitch} checked={showRuntime} />
          <FormCheck.Label htmlFor={`chk-${controlId}`}>Dynamic</FormCheck.Label>
        </FormCheck>
      </div>
    </FormGroup>
  );
};

FormInputRuntimeDate.defaultProps = {
  disabled: false,
  hidden: false,
  noLabel: false,
  touched: false,
  trackDirty: true,
  valid: true,
};

FormInputRuntimeDate.propTypes = {
  controlId: PropTypes.string,
  disabled: PropTypes.bool,
  errorMessage: PropTypes.string,
  field: PropTypes.object,
  hidden: PropTypes.bool,
  noLabel: PropTypes.bool,
  onChange: PropTypes.func,
  originalValue: PropTypes.string,
  touched: PropTypes.bool,
  trackDirty: PropTypes.bool,
  valid: PropTypes.bool,
  value: PropTypes.string,
};

export default FormInputRuntimeDate;
